import * as localforage from 'localforage';
import type { 
  NamedVersion, 
  VersionRepository, 
  StorageConfig,
  VersionState,
  HistoryAction
} from './types.ts';
import { STORAGE_CONFIG, STORAGE_KEYS } from './constants.ts';

/**
 * Abstract Storage Layer - ORM-like Repository Pattern
 * This can be easily swapped for a database implementation later
 */

// Generate unique IDs
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// LocalForage implementation of VersionRepository
class LocalForageVersionRepository implements VersionRepository {
  private versionsStore: LocalForage;
  private settingsStore: LocalForage;

  constructor(config: StorageConfig) {
    // Configure LocalForage instances
    this.versionsStore = localforage.createInstance({
      name: config.DB_NAME,
      storeName: config.STORES.VERSIONS,
      version: config.VERSION,
      description: 'Stores named versions of seating charts'
    });

    this.settingsStore = localforage.createInstance({
      name: config.DB_NAME,
      storeName: config.STORES.SETTINGS,
      version: config.VERSION,
      description: 'Stores application settings and metadata'
    });
  }

  async getAll(): Promise<NamedVersion[]> {
    try {
      const versions: NamedVersion[] = [];
      await this.versionsStore.iterate<NamedVersion, void>((value: NamedVersion) => {
        versions.push(this.deserializeVersion(value));
      });
      
      // Sort by lastModified (most recent first)
      return versions.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());
    } catch (error) {
      console.error('Failed to get all versions:', error);
      return [];
    }
  }

  async getById(id: string): Promise<NamedVersion | null> {
    try {
      const version = await this.versionsStore.getItem<NamedVersion>(id);
      return version ? this.deserializeVersion(version) : null;
    } catch (error) {
      console.error(`Failed to get version ${id}:`, error);
      return null;
    }
  }

  async getLastUsed(): Promise<string | null> {
    try {
      return await this.settingsStore.getItem<string>(STORAGE_KEYS.LAST_USED_VERSION);
    } catch (error) {
      console.error('Failed to get last used version:', error);
      return null;
    }
  }

  async create(versionData: Omit<NamedVersion, 'id' | 'createdAt' | 'lastModified'>): Promise<NamedVersion> {
    const now = new Date();
    const version: NamedVersion = {
      ...versionData,
      id: generateId(),
      createdAt: now,
      lastModified: now
    };

    try {
      await this.versionsStore.setItem(version.id, this.serializeVersion(version));
      await this.setLastUsed(version.id);
      return version;
    } catch (error) {
      console.error('Failed to create version:', error);
      throw new Error('Failed to create version');
    }
  }

  async update(id: string, updates: Partial<NamedVersion>): Promise<NamedVersion> {
    try {
      const existing = await this.getById(id);
      if (!existing) {
        throw new Error(`Version ${id} not found`);
      }

      const updated: NamedVersion = {
        ...existing,
        ...updates,
        id, // Ensure ID cannot be changed
        lastModified: new Date()
      };

      await this.versionsStore.setItem(id, this.serializeVersion(updated));
      await this.setLastUsed(id);
      return updated;
    } catch (error) {
      console.error(`Failed to update version ${id}:`, error);
      throw new Error('Failed to update version');
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await this.versionsStore.removeItem(id);
      
      // If this was the last used version, clear that setting
      const lastUsed = await this.getLastUsed();
      if (lastUsed === id) {
        await this.settingsStore.removeItem(STORAGE_KEYS.LAST_USED_VERSION);
      }
    } catch (error) {
      console.error(`Failed to delete version ${id}:`, error);
      throw new Error('Failed to delete version');
    }
  }

  async setLastUsed(id: string): Promise<void> {
    try {
      await this.settingsStore.setItem(STORAGE_KEYS.LAST_USED_VERSION, id);
    } catch (error) {
      console.error(`Failed to set last used version ${id}:`, error);
    }
  }

  // Settings management
  async getSetting<T>(key: string): Promise<T | null> {
    try {
      return await this.settingsStore.getItem<T>(key);
    } catch (error) {
      console.error(`Failed to get setting ${key}:`, error);
      return null;
    }
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    try {
      await this.settingsStore.setItem(key, value);
    } catch (error) {
      console.error(`Failed to set setting ${key}:`, error);
    }
  }

  // Serialization helpers to handle Date objects
  private serializeVersion(version: NamedVersion): any {
    return {
      ...version,
      createdAt: version.createdAt.toISOString(),
      lastModified: version.lastModified.toISOString(),
      history: version.history.map(entry => ({
        ...entry,
        timestamp: entry.timestamp.toISOString()
      }))
    };
  }

  private deserializeVersion(data: any): NamedVersion {
    return {
      ...data,
      createdAt: new Date(data.createdAt),
      lastModified: new Date(data.lastModified),
      history: (data.history || []).map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp)
      }))
    };
  }
}

// Storage Manager - Main interface for the app
export class StorageManager {
  private versionRepo: VersionRepository;

  constructor(config: StorageConfig = STORAGE_CONFIG) {
    this.versionRepo = new LocalForageVersionRepository(config);
  }

  // Version management
  async getAllVersions(): Promise<NamedVersion[]> {
    return this.versionRepo.getAll();
  }

  async getVersion(id: string): Promise<NamedVersion | null> {
    return this.versionRepo.getById(id);
  }

  async getLastUsedVersion(): Promise<NamedVersion | null> {
    const lastUsedId = await this.versionRepo.getLastUsed();
    if (!lastUsedId) return null;
    return this.getVersion(lastUsedId);
  }

  async createVersion(name: string, state: VersionState, initialHistoryEntry?: HistoryAction): Promise<NamedVersion> {
    const defaultHistoryEntry: HistoryAction = {
      id: generateId(),
      timestamp: new Date(),
      type: 'version_created',
      description: 'Version created',
      beforeState: { guests: [], customFields: [] },
      afterState: state
    };

    return this.versionRepo.create({
      name,
      state,
      history: [initialHistoryEntry || defaultHistoryEntry]
    });
  }

  async updateVersionState(id: string, state: VersionState, historyEntry: HistoryAction): Promise<NamedVersion> {
    const version = await this.getVersion(id);
    if (!version) {
      throw new Error(`Version ${id} not found`);
    }

    return this.versionRepo.update(id, {
      state,
      history: [...version.history, historyEntry]
    });
  }

  async updateVersionHistory(id: string, history: HistoryAction[]): Promise<NamedVersion> {
    const version = await this.getVersion(id);
    if (!version) {
      throw new Error(`Version ${id} not found`);
    }

    return this.versionRepo.update(id, {
      history
    });
  }

  async updateVersionStateAndHistory(id: string, state: VersionState, history: HistoryAction[]): Promise<NamedVersion> {
    const version = await this.getVersion(id);
    if (!version) {
      throw new Error(`Version ${id} not found`);
    }

    return this.versionRepo.update(id, {
      state,
      history,
      lastModified: new Date()
    });
  }

  async renameVersion(id: string, newName: string): Promise<NamedVersion> {
    return this.versionRepo.update(id, { name: newName });
  }

  async deleteVersion(id: string): Promise<void> {
    return this.versionRepo.delete(id);
  }

  async setLastUsedVersion(id: string): Promise<void> {
    return this.versionRepo.setLastUsed(id);
  }

  // Utility methods
  async isVersionNameTaken(name: string, excludeId?: string): Promise<boolean> {
    const versions = await this.getAllVersions();
    return versions.some(v => v.name === name && v.id !== excludeId);
  }

  // Sidebar state management
  async getSidebarCollapsed(): Promise<boolean> {
    const collapsed = await this.versionRepo.getSetting<boolean>(STORAGE_KEYS.SIDEBAR_COLLAPSED);
    return collapsed ?? false; // Default to expanded
  }

  async setSidebarCollapsed(collapsed: boolean): Promise<void> {
    await this.versionRepo.setSetting(STORAGE_KEYS.SIDEBAR_COLLAPSED, collapsed);
  }
}

// Export singleton instance
export const storageManager = new StorageManager();

// Export for testing or alternative configurations
export { LocalForageVersionRepository };